import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/app/DataTable";
import { StatusBadge } from "@/components/app/common";
import { api, errMsg } from "@/lib/api";

const TARGETS = [["cancelled", "Cancelled"], ["approved", "Approved"], ["revive", "Revive (back to status before cancellation)"]];
const COLS = [
  { key: "order_no", label: "LM No.", mono: true }, { key: "customer", label: "Customer", wrap: true },
  { key: "current_status", label: "Current status", render: (r) => (r.current_status ? <StatusBadge status={r.current_status} /> : "—") },
  { key: "new_status", label: "New status", exp: (r) => r.new_status || "Skipped", render: (r) => (r.new_status ? <StatusBadge status={r.new_status} /> : <span className="text-xs text-red-700">Skipped</span>) },
  { key: "result", label: "Result", render: (r) => <StatusBadge status={r.result} /> },
  { key: "message", label: "Message", wrap: true },
  { key: "warnings", label: "Warnings", wrap: true, exp: (r) => (r.warnings || []).join("; "), render: (r) => <span className="text-xs text-amber-800">{(r.warnings || []).join("; ") || "—"}</span> },
];

function Confirm({ n, value, onChange }) {
  if (n <= 20) return null;
  return (
    <div className="rounded border border-red-200 bg-red-50 p-2" data-testid="bulk-status-confirm-box">
      <Label className="text-xs text-red-800">This run changes {n} LMs. Type {n} to confirm</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-32" data-testid="bulk-status-confirm-input" />
    </div>
  );
}

// LM Register: Admin-only bulk cancel / approve / revive - preview then commit
export function BulkStatusDialog({ ids, onClose, onDone }) {
  const [to, setTo] = useState("cancelled");
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { if (!out?.committed) { setOut(null); setTyped(""); } };
  const run = async (commit) => {
    if (reason.trim().length < 3) return toast.error("Enter the reason (at least 3 characters)");
    setBusy(true);
    try {
      const r = await api.post(`/orders/bulk-status?commit=${commit}`, { ids, to, reason });
      setOut(r.data);
      if (commit) { toast.success(`${r.data.changed} changed · ${r.data.skipped} skipped`); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const n = out?.will_change || 0;
  const ok = out && !out.committed && n > 0 && (n <= 20 || typed.trim() === String(n));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="bulk-status-dialog">
        <DialogHeader><DialogTitle>Bulk change status - {ids.length} LM(s)</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs">New status *</Label>
            <select className="h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm" value={to} disabled={out?.committed} onChange={(e) => { setTo(e.target.value); reset(); }} data-testid="bulk-status-target">
              {TARGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><Label className="text-xs">Reason (applies to all) *</Label>
            <Input value={reason} disabled={out?.committed} onChange={(e) => { setReason(e.target.value); reset(); }} placeholder="Why are these LMs changing?" data-testid="bulk-status-reason" /></div>
        </div>
        {out && <>
          <p className="text-sm font-semibold" data-testid="bulk-status-summary">{out.committed ? `Result: changed ${out.changed} / skipped ${out.skipped}` : `Preview - nothing changed yet: will change ${out.will_change} / skipped ${out.skipped}`}</p>
          <DataTable testid="bulk-status-table" columns={COLS} rows={out.results} exportName={out.committed ? "LM_Bulk_Status_Result" : "LM_Bulk_Status_Preview"} exportPerm="orders.export" />
          {!out.committed && <Confirm n={n} value={typed} onChange={setTyped} />}
        </>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="bulk-status-close">{out?.committed ? "Close" : "Cancel"}</Button>
          {!out?.committed && <Button variant="outline" disabled={busy} onClick={() => run(false)} data-testid="bulk-status-preview-btn">Preview</Button>}
          {!out?.committed && <Button disabled={busy || !ok} onClick={() => run(true)} data-testid="bulk-status-commit-btn">Apply to {n} LM(s)</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
